import { useState, useEffect, useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { useFileLinksTarget } from '../hooks/useFileLinksTarget'
import { usePermissions, Permission } from '../hooks/usePermissions'
import { useToast } from './ToastContainer'
import { updateIssue, updateIssueCustomField, deleteIssue, archiveIssue, moveIssue } from '../store/slices/issueSlice'
import { fetchComments, createComment, updateComment, deleteComment } from '../store/slices/commentSlice'
import { fetchActivities } from '../store/slices/activitySlice'
import { updateIssueLabels } from '../store/slices/labelSlice'
import { fetchProjects } from '../store/slices/projectSlice'
import { Issue, IssuePriority, IssueStatus } from '@/types'
import { formatDateTimeWithSeconds } from '../utils/formatters'
import * as customFieldApi from '../services/api/customField'
import OfficialFroalaEditor from './OfficialFroalaEditor'
import CommentSection from './CommentSection'
import ActivityLog from './ActivityLog'
import DatePicker from './DatePicker'
import LabelPicker from './LabelPicker'
import {
  XMarkIcon,
  MinusIcon,
  UserCircleIcon,
  CalendarIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  InformationCircleIcon,
  ListBulletIcon,
  ShareIcon,
  EllipsisVerticalIcon,
  LinkIcon,
  ArchiveBoxIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronDoubleUpIcon,
  ChevronDoubleDownIcon,
  ArrowLeftIcon,
  ChevronLeftIcon
} from '@heroicons/react/24/outline'
import { Menu, Transition } from '@headlessui/react'
import { Fragment } from 'react'

interface IssueDetailModalProps {
  issue: Issue | null
  isOpen: boolean
  onClose: () => void
}

const priorityConfig = {
  [IssuePriority.HIGHEST]: {
    icon: ChevronDoubleUpIcon,
    color: 'text-red-500',
    bg: 'bg-red-50',
    label: '最高',
  },
  [IssuePriority.HIGH]: {
    icon: ChevronUpIcon,
    color: 'text-orange-500',
    bg: 'bg-orange-50',
    label: '高',
  },
  [IssuePriority.MEDIUM]: {
    icon: MinusIcon,
    color: 'text-yellow-500',
    bg: 'bg-yellow-50',
    label: '中',
  },
  [IssuePriority.LOW]: {
    icon: ChevronDownIcon,
    color: 'text-green-500',
    bg: 'bg-green-50',
    label: '低',
  },
  [IssuePriority.LOWEST]: {
    icon: ChevronDoubleDownIcon,
    color: 'text-blue-500',
    bg: 'bg-blue-50',
    label: '最低',
  },
}

const statusConfig = {
  [IssueStatus.TODO]: { label: '待辦', color: 'bg-gray-100 text-gray-800' },
  [IssueStatus.IN_PROGRESS]: { label: '進行中', color: 'bg-blue-100 text-blue-800' },
  [IssueStatus.IN_REVIEW]: { label: '待審核', color: 'bg-yellow-100 text-yellow-800' },
  [IssueStatus.DONE]: { label: '完成', color: 'bg-green-100 text-green-800' },
}

const IssueDetailModal = ({ issue: propIssue, isOpen, onClose }: IssueDetailModalProps) => {
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editingValue, setEditingValue] = useState<string>('')
  const [activeTab, setActiveTab] = useState<'details' | 'comments' | 'activity'>('details')
  const [editData, setEditData] = useState({
    title: '',
    description: '',
    priority: IssuePriority.MEDIUM,
    status: IssueStatus.TODO,
    assigneeId: null as string | null,
  })
  const [projectCustomFields, setProjectCustomFields] = useState<customFieldApi.ProjectCustomField[]>([])
  const dispatch = useAppDispatch()
  const { user, isAuthenticated } = useAppSelector((state) => state.auth)
  const { comments } = useAppSelector((state) => state.comment)
  const { activities, loading: activityLoading } = useAppSelector((state) => state.activity)
  const { currentProject, projects } = useAppSelector((state) => state.project)
  const { issues } = useAppSelector((state) => state.issue)
  const { showSuccess, showError } = useToast()
  
  // 從 Redux store 中獲取最新的議題資料，如果找不到則使用 prop
  const issue = propIssue ? (issues.find(i => i.id === propIssue.id) || propIssue) : null
  
  // 專案權限檢查
  const { hasPermission } = usePermissions(issue?.projectId)
  const canEditIssue = hasPermission(Permission.EDIT_ISSUE)
  const canDeleteIssue = hasPermission(Permission.DELETE_ISSUE)
  const canArchiveIssue = hasPermission(Permission.ARCHIVE_ISSUE)
  const canAssignIssue = hasPermission(Permission.ASSIGN_ISSUE)
  const canCreateComment = hasPermission(Permission.CREATE_COMMENT)
  const canEditComment = hasPermission(Permission.EDIT_COMMENT)
  const canDeleteComment = hasPermission(Permission.DELETE_COMMENT)
  
  // 使用 Hook 來處理檔案連結
  const descriptionRef = useFileLinksTarget(issue?.description || '')

  // 載入專案列表用於移動功能
  useEffect(() => {
    if (isAuthenticated && projects.length === 0) {
      dispatch(fetchProjects())
    }
  }, [dispatch, isAuthenticated, projects.length])

  useEffect(() => {
    if (issue) {
      // 只有在非編輯模式下才更新 editData，避免覆蓋用戶正在編輯的內容
      if (!editingField) {
        const newData = {
          title: issue.title,
          description: issue.description || '',
          priority: issue.priority,
          status: issue.status,
          assigneeId: issue.assigneeId || null,
        }
        setEditData(newData)
      }
      
      // 只有在已認證時才載入留言和活動記錄
      if (isAuthenticated) {
        dispatch(fetchComments(issue.id))
        dispatch(fetchActivities(issue.id))
      }
    }
  }, [issue?.id, dispatch, isAuthenticated])

  // 當議題更新後，確保編輯數據也同步更新（但不在編輯模式下）
  useEffect(() => {
    if (issue && !editingField) {
      setEditData(_prevData => ({
        title: issue.title,
        description: issue.description || '',
        priority: issue.priority,
        status: issue.status,
        assigneeId: issue.assigneeId || null,
      }))
    }
  }, [issue?.title, issue?.description, issue?.priority, issue?.status, issue?.assigneeId, editingField])

  // 當模態框關閉時重置狀態
  useEffect(() => {
    if (!isOpen) {
      setEditingField(null)
      setProjectCustomFields([])
    }
  }, [isOpen])

  // 載入專案自訂欄位配置
  useEffect(() => {
    if (issue?.projectId && isOpen) {
      loadProjectCustomFields()
    }
  }, [issue?.projectId, isOpen])

  const loadProjectCustomFields = async () => {
    if (!issue?.projectId) return

    try {
      const data = await customFieldApi.getProjectCustomFields(issue.projectId)
      setProjectCustomFields(data.enabledFields || [])
    } catch (error) {
      console.error('載入專案自訂欄位失敗:', error)
      // 如果是認證錯誤，這裡可以進行特殊處理
      if (error instanceof Error && error.message.includes('401')) {
        console.warn('自訂欄位載入失敗：使用者未認證')
      }
    } finally {
    }
  }

  // const handleSave = async () => {
  //   if (!issue || !canEditIssue) return

  //   try {
  //     await dispatch(updateIssue({
  //       issueId: issue.id,
  //       updateData: editData
  //     })).unwrap()
  //     setEditingField(null)
  //   } catch (error) {
  //     console.error('更新議題失敗:', error)
  //   }
  // }

  // const handleCancel = () => {
  //   if (issue) {
  //     setEditData({
  //       title: issue.title,
  //       description: issue.description || '',
  //       priority: issue.priority,
  //       status: issue.status,
  //       assigneeId: issue.assigneeId || null,
  //     })
  //   }
  //   setEditingField(null)
  // }

  const handleFieldClick = useCallback((fieldName: string) => {
    // 檢查編輯權限
    if (!canEditIssue) return
    
    // 檢查指派權限（如果是 assignee 欄位）
    if (fieldName === 'assignee' && !canAssignIssue) return
    
    // 先清空編輯狀態，然後設置新的編輯欄位
    setEditingField(null)
    
    // 使用 setTimeout 確保狀態更新完成後再設置新的編輯欄位
    setTimeout(() => {
      setEditingField(fieldName)
    }, 10)
  }, [canEditIssue, canAssignIssue, editingField])

  const handleFieldSave = async (fieldName: string, value: any) => {
    if (!issue) return

    try {
      const updateData = { [fieldName]: value }
      await dispatch(updateIssue({
        issueId: issue.id,
        updateData
      })).unwrap()
      
      // 退出編輯模式
      setEditingField(null)
    } catch (error) {
      console.error('更新議題失敗:', error)
    }
  }

  // 處理標籤更新
  const handleLabelsUpdate = async (labels: any[]) => {
    console.log('handleLabelsUpdate called with:', labels)
    if (!issue) {
      console.log('No issue found, returning')
      return
    }

    try {
      const labelIds = labels.map(label => label.id)
      console.log('Label IDs to update:', labelIds)
      console.log('Issue ID:', issue.id)

      const result = await dispatch(updateIssueLabels({
        issueId: issue.id,
        labelIds
      })).unwrap()

      console.log('Update labels result:', result)
      showSuccess('標籤更新成功')
    } catch (error) {
      console.error('更新標籤失敗:', error)
      showError('更新失敗', '無法更新議題標籤')
    }
  }

  // 處理自訂欄位值更新
  const handleCustomFieldUpdate = async (fieldId: string, value: string | number | null) => {
    if (!issue || !canEditIssue) return

    try {
      // 使用 Redux action 更新自訂欄位值
      await dispatch(updateIssueCustomField({
        issueId: issue.id,
        fieldId,
        value
      })).unwrap()

      showSuccess('自訂欄位更新成功')
    } catch (error) {
      console.error('更新自訂欄位失敗:', error)
      showError('更新失敗', '無法更新自訂欄位')
    }
  }

  // 取得自訂欄位的當前值
  const getCustomFieldValue = (fieldId: string) => {
    const fieldValue = issue?.customFieldValues?.find(cfv => cfv.fieldId === fieldId)
    if (!fieldValue) return null

    // 根據欄位類型返回對應的值
    if (fieldValue.value !== null) return fieldValue.value
    if (fieldValue.numberValue !== null) return fieldValue.numberValue
    if (fieldValue.dateValue !== null) return fieldValue.dateValue
    return null
  }

  // 取得欄位類型標籤
  const getFieldTypeLabel = (type: string) => {
    switch (type) {
      case 'TEXT': return '文字'
      case 'NUMBER': return '數字'
      case 'DATE': return '日期'
      case 'SELECT': return '下拉選單'
      default: return type
    }
  }


  const handleKeyPress = (e: React.KeyboardEvent, fieldName: string) => {
    // 對於 Froala Editor，我們不需要處理 Enter 鍵，因為它有自己的內部處理
    if (fieldName === 'description') {
      // Froala Editor 有自己的鍵盤處理，只處理 ESC 鍵
      if (e.key === 'Escape') {
        if (issue) {
          setEditData({
            title: issue.title,
            description: issue.description || '',
            priority: issue.priority,
            status: issue.status,
            assigneeId: issue.assigneeId || null,
          })
        }
        setEditingField(null)
      }
      return
    }

    // 對於其他欄位（如 title），保持原有邏輯
    const target = e.target as HTMLInputElement
    const isComposing = (target as any).isComposing || (e.nativeEvent && (e.nativeEvent as any).isComposing)
    
    if (e.key === 'Enter') {
      if (isComposing) {
        // 如果正在輸入中文，不阻止預設行為，讓輸入法處理
        return
      }
      
      // 只有在非組合狀態下才觸發保存
      e.preventDefault()
      handleFieldSave(fieldName, editData[fieldName as keyof typeof editData])
    } else if (e.key === 'Escape') {
      // ESC 鍵取消編輯並恢復原值，無論是否在組合狀態
      if (issue) {
        setEditData({
          title: issue.title,
          description: issue.description || '',
          priority: issue.priority,
          status: issue.status,
          assigneeId: issue.assigneeId || null,
        })
      }
      setEditingField(null)
    }
  }

  // 留言處理函數
  const handleAddComment = async (content: string) => {
    if (!issue) return
    await dispatch(createComment({ content, issueId: issue.id })).unwrap()
  }

  const handleUpdateComment = async (commentId: string, content: string) => {
    if (!issue) return
    await dispatch(updateComment({ commentId, content, issueId: issue.id })).unwrap()
  }

  const handleDeleteComment = async (commentId: string) => {
    if (!issue) return
    await dispatch(deleteComment({ commentId, issueId: issue.id })).unwrap()
  }

  // 處理複製分享連結
  const handleCopyLink = async () => {
    if (!issue?.id) return
    const url = `${window.location.origin}/issues/${issue.id}`
    try {
      await navigator.clipboard.writeText(url)
      showSuccess('已複製連結')
    } catch (error) {
      console.error('複製連結失敗:', error)
      showError('複製失敗', '無法複製連結到剪貼板')
    }
  }

  // 處理封存議題
  const handleArchive = async () => {
    if (!issue?.id || !canArchiveIssue) return

    const isArchiving = !issue.archived
    const action = isArchiving ? '封存' : '取消封存'

    try {
      await dispatch(archiveIssue({ issueId: issue.id, archived: isArchiving })).unwrap()
      showSuccess(`已${action}議題`, `議題「${issue?.title || ''}」已${action}`)
      // 如果封存成功，關閉模態框
      if (isArchiving) {
        onClose()
      }
    } catch (error) {
      console.error(`${action}議題失敗:`, error)
      showError(`${action}失敗`, `無法${action}議題`)
    }
  }

  // 處理刪除議題
  const handleDelete = async () => {
    if (!issue?.id || !canDeleteIssue) return

    try {
      await dispatch(deleteIssue(issue.id)).unwrap()
      showSuccess('已移至回收桶', `議題「${issue?.title || ''}」已移至回收桶`)
      onClose() // 關閉模態框
    } catch (error) {
      console.error('刪除議題失敗:', error)
      showError('刪除失敗', '無法刪除議題')
    }
  }

  // 處理移動議題到其他專案
  const handleMoveToProject = async (targetProjectId: string) => {
    if (!issue?.id || !canEditIssue) return

    const targetProject = projects.find(p => p.id === targetProjectId)
    if (!targetProject) return

    try {
      const result = await dispatch(moveIssue({
        issueId: issue.id,
        targetProjectId
      })).unwrap() as { issue?: Issue; data?: { issue?: Issue } }

      const movedIssue = result.issue ?? result.data?.issue ?? null

      if (!movedIssue) {
        showError('移動失敗', '無法取得更新後的議題資訊，請稍後再試')
        return
      }

      showSuccess(
        '議題已成功移動',
        `議題「${issue.title}」已移動到專案「${targetProject.name}」，新編號：${movedIssue.key}`
      )

      // 關閉模態框，讓用戶回到看板
      onClose()
    } catch (error) {
      console.error('移動議題失敗:', error)
      showError('移動失敗', '無法移動議題到目標專案')
    }
  }

  // 計算可移動的目標專案
  const availableProjects = projects.filter(project => {
    if (project.id === issue?.projectId) return false // 排除當前專案

    // 檢查是否為專案成員或管理員
    const isProjectMember = project.members?.some(member => member.userId === user?.id)
    const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN'

    return isProjectMember || isAdmin
  })

  if (!isOpen || !issue) return null

  const priority = priorityConfig[issue.priority]
  const status = statusConfig[issue.status]
  const PriorityIcon = priority.icon

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="inline-block w-full max-w-4xl p-4 md:p-6 my-4 md:my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <div className="flex items-center space-x-2 md:space-x-3 flex-wrap">
              <span className="text-sm font-medium text-gray-500">{issue.key}</span>
              <div className={`p-1 rounded ${priority.bg}`}>
                <PriorityIcon className={`h-3 md:h-4 w-3 md:w-4 ${priority.color}`} />
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.color}`}>
                {status.label}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {/* 分享按鈕 */}
              <button
                onClick={handleCopyLink}
                className="p-1.5 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded transition-colors duration-150"
                title="複製分享連結"
              >
                <ShareIcon className="w-4 md:w-5 h-4 md:h-5" />
              </button>
              
              {/* 三個點選單 */}
              <Menu as="div" className="relative inline-block text-left">
                <Menu.Button className="p-1.5 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded transition-colors duration-150">
                  <EllipsisVerticalIcon className="w-4 md:w-5 h-4 md:h-5" />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-20 w-44 mt-1 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={handleCopyLink}
                            className={`${
                              active ? 'bg-gray-100' : ''
                            } group flex items-center w-full px-3 py-2 text-sm text-gray-700`}
                          >
                            <LinkIcon className="w-4 h-4 mr-2 text-gray-500" />
                            複製分享連結
                          </button>
                        )}
                      </Menu.Item>
                      {/* 封存議題 - 需要權限 */}
                      {canArchiveIssue && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleArchive}
                              className={`${
                                active ? 'bg-gray-100' : ''
                              } group flex items-center w-full px-3 py-2 text-sm text-gray-700`}
                            >
                              <ArchiveBoxIcon className="w-4 h-4 mr-2 text-gray-500" />
                              {issue.archived ? '取消封存' : '封存議題'}
                            </button>
                          )}
                        </Menu.Item>
                      )}

                      {/* 移動到其他專案 - 需要編輯權限 */}
                      {canEditIssue && (
                        <Menu.Item>
                          {({ active }) => (
                            <div className="relative group">
                              <button
                                className={`${
                                  active ? 'bg-gray-100' : ''
                                } group flex items-center w-full px-3 py-2 text-sm text-gray-700 justify-between`}
                              >
                                <span className="flex items-center">
                                  <ArrowLeftIcon className="w-4 h-4 mr-2 text-gray-500" />
                                  移動到其他專案
                                </span>
                                <ChevronLeftIcon className="w-3 h-3 text-gray-400" />
                              </button>

                              {/* 浮動子選單 */}
                              <div className="absolute right-full top-0 mr-1 hidden group-hover:block z-[9999]">
                                <div className="bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 min-w-[200px] max-h-60 overflow-y-auto">
                                  {availableProjects.length > 0 ? (
                                    availableProjects.map(project => (
                                      <button
                                        key={project.id}
                                        onClick={() => handleMoveToProject(project.id)}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                        title={`移動到專案: ${project.name}`}
                                      >
                                        <div className="truncate">{project.name}</div>
                                        <div className="text-xs text-gray-500 truncate">{project.key}</div>
                                      </button>
                                    ))
                                  ) : (
                                    <div className="px-4 py-3 text-sm text-gray-500 italic">
                                      缺少其他專案權限
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </Menu.Item>
                      )}

                      {/* 刪除議題 - 需要權限 */}
                      {canDeleteIssue && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleDelete}
                              className={`${
                                active ? 'bg-red-50' : ''
                              } group flex items-center w-full px-3 py-2 text-sm text-red-600`}
                            >
                              <TrashIcon className="w-4 h-4 mr-2 text-red-500" />
                              刪除議題
                            </button>
                          )}
                        </Menu.Item>
                      )}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
              
              {/* 關閉按鈕 */}
              <button
                onClick={onClose}
                className="p-1.5 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded transition-colors duration-150"
              >
                <XMarkIcon className="w-4 md:w-5 h-4 md:h-5" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-4 md:mb-6">
            <nav className="-mb-px flex space-x-4 md:space-x-8 overflow-x-auto">
              <button
                onClick={() => setActiveTab('details')}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                  activeTab === 'details'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <InformationCircleIcon className="w-4 h-4" />
                <span>詳細資訊</span>
              </button>
              <button
                onClick={() => setActiveTab('comments')}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                  activeTab === 'comments'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <ChatBubbleLeftRightIcon className="w-4 h-4" />
                <span>({comments[issue.id]?.length || 0})</span>
              </button>
              <button
                onClick={() => setActiveTab('activity')}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                  activeTab === 'activity'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <ListBulletIcon className="w-4 h-4" />
                <span>活動記錄 ({activities[issue.id]?.length || 0})</span>
              </button>
            </nav>
          </div>

          {/* Content - 響應式布局 */}
          {activeTab === 'details' ? (
            <div className="flex flex-col lg:grid lg:grid-cols-3 lg:gap-8 space-y-6 lg:space-y-0">
              {/* 主要內容 */}
              <div className="lg:col-span-2 space-y-4 md:space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    標題
                  </label>
                  {editingField === 'title' ? (
                    <div>
                      <input
                        type="text"
                        value={editData.title}
                        onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                        onKeyDown={(e) => handleKeyPress(e, 'title')}
                        onCompositionStart={(e) => {
                          // 開始中文輸入法組合
                          (e.target as any).isComposing = true
                        }}
                        onCompositionEnd={(e) => {
                          // 結束中文輸入法組合
                          (e.target as any).isComposing = false
                        }}
                        className="w-full px-3 py-2 text-sm md:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        autoFocus
                      />
                      <div className="mt-2 flex justify-end space-x-2">
                        <button
                          onClick={() => setEditingField(null)}
                          className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                        >
                          取消
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              await handleFieldSave('title', editData.title)
                            } catch (error) {
                              console.error('Save failed:', error)
                            }
                          }}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          保存
                        </button>
                      </div>
                    </div>
                  ) : (
                    <h2 
                      className={`text-xl font-semibold text-gray-900 px-2 py-1 rounded transition-colors ${
                        canEditIssue 
                          ? 'cursor-pointer hover:bg-gray-50' 
                          : 'cursor-not-allowed opacity-75'
                      }`}
                      onClick={() => handleFieldClick('title')}
                      title={canEditIssue ? "點擊編輯標題" : "無編輯權限"}
                    >
                      {issue.title}
                    </h2>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    描述
                  </label>
                  {editingField === 'description' ? (
                    <div className="relative">
                      <OfficialFroalaEditor
                        key={`description-editor-${issue?.id}-${editingField}`}
                        value={editData.description}
                        onChange={(value) => {
                          setEditData({ ...editData, description: value })
                        }}
                        placeholder="輸入議題描述"
                        autoFocus
                        height={300}
                        issueId={issue?.id}
                      />
                      <div className="mt-2 flex justify-end space-x-2">
                        <button
                          onClick={() => setEditingField(null)}
                          className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                        >
                          取消
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              await handleFieldSave('description', editData.description)
                            } catch (error) {
                              console.error('Save failed:', error)
                            }
                          }}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          保存
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className={`text-gray-700 pl-8 pr-3 py-3 rounded transition-colors min-h-[5rem] border border-gray-200 ${
                        canEditIssue 
                          ? 'cursor-pointer hover:bg-gray-50' 
                          : 'cursor-not-allowed opacity-75'
                      }`}
                      onClick={() => handleFieldClick('description')}
                      title={canEditIssue ? "點擊編輯描述" : "無編輯權限"}
                      style={{ overflow: 'visible' }}
                    >
                      {issue.description ? (
                        <div 
                          ref={descriptionRef}
                          className="prose prose-sm max-w-none"
                          style={{ 
                            marginLeft: '-1rem',
                            overflow: 'visible',
                            position: 'relative'
                          }}
                          dangerouslySetInnerHTML={{ __html: issue.description }}
                        />
                      ) : (
                        <span className="text-gray-500 italic">點擊新增描述...</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Mobile Issue Details - 手機版議題詳情在描述下方 */}
                <div className="lg:hidden">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-base font-medium text-gray-900 mb-4">議題詳情</h3>
                    
                    <div className="space-y-4">
                      {/* Priority */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          優先級
                        </label>
                        {editingField === 'priority' ? (
                          <select
                            value={editData.priority}
                            onChange={(e) => {
                              const newPriority = e.target.value as IssuePriority
                              setEditData({ ...editData, priority: newPriority })
                              handleFieldSave('priority', newPriority)
                            }}
                            onBlur={() => setEditingField(null)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            autoFocus
                          >
                            <option value={IssuePriority.HIGHEST}>最高</option>
                            <option value={IssuePriority.HIGH}>高</option>
                            <option value={IssuePriority.MEDIUM}>中</option>
                            <option value={IssuePriority.LOW}>低</option>
                            <option value={IssuePriority.LOWEST}>最低</option>
                          </select>
                        ) : (
                          <div 
                            className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 px-2 py-2 rounded transition-colors"
                            onClick={() => handleFieldClick('priority')}
                            title="點擊修改優先級"
                          >
                            <div className={`p-1 rounded ${priority.bg}`}>
                              <PriorityIcon className={`h-4 w-4 ${priority.color}`} />
                            </div>
                            <span className="text-sm text-gray-700">{priority.label}</span>
                          </div>
                        )}
                      </div>

                      {/* Status */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          狀態
                        </label>
                        {editingField === 'status' ? (
                          <select
                            value={editData.status}
                            onChange={(e) => {
                              const newStatus = e.target.value as IssueStatus
                              setEditData({ ...editData, status: newStatus })
                              handleFieldSave('status', newStatus)
                            }}
                            onBlur={() => setEditingField(null)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            autoFocus
                          >
                            <option value={IssueStatus.TODO}>待辦</option>
                            <option value={IssueStatus.IN_PROGRESS}>進行中</option>
                            <option value={IssueStatus.IN_REVIEW}>待審核</option>
                            <option value={IssueStatus.DONE}>完成</option>
                          </select>
                        ) : (
                          <span 
                            className={`px-2 py-1 text-xs font-medium rounded-full cursor-pointer hover:bg-opacity-75 transition-colors block text-center w-fit ${status.color}`}
                            onClick={() => handleFieldClick('status')}
                            title="點擊修改狀態"
                          >
                            {status.label}
                          </span>
                        )}
                      </div>

                      {/* Assignee */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          負責人
                        </label>
                        {editingField === 'assignee' ? (
                          <select
                            value={editData.assigneeId || ''}
                            onChange={(e) => {
                              const assigneeId = e.target.value || null
                              setEditData({ ...editData, assigneeId })
                              handleFieldSave('assigneeId', assigneeId)
                            }}
                            onBlur={() => setEditingField(null)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            autoFocus
                          >
                            <option value="">未指派</option>
                            {currentProject?.members?.map((member) => (
                              <option key={member.user.id} value={member.user.id}>
                                {member.user.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div 
                            className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 px-2 py-2 rounded transition-colors"
                            onClick={() => handleFieldClick('assignee')}
                            title="點擊指派負責人"
                          >
                            {issue.assignee ? (
                              <>
                                {issue.assignee.avatar ? (
                                  <img
                                    src={issue.assignee.avatar}
                                    alt={issue.assignee.name}
                                    className="w-6 h-6 rounded-full"
                                  />
                                ) : (
                                  <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
                                    <span className="text-xs font-medium text-gray-600">
                                      {issue.assignee.name.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                )}
                                <span className="text-sm text-gray-700">{issue.assignee.name}</span>
                              </>
                            ) : (
                              <>
                                <UserCircleIcon className="w-6 h-6 text-gray-400" />
                                <span className="text-sm text-gray-500">未指派</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Reporter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          報告人
                        </label>
                        <div className="flex items-center space-x-2">
                          {issue.reporter.avatar ? (
                            <img
                              src={issue.reporter.avatar}
                              alt={issue.reporter.name}
                              className="w-6 h-6 rounded-full"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-xs font-medium text-gray-600">
                                {issue.reporter.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <span className="text-sm text-gray-700">{issue.reporter.name}</span>
                        </div>
                      </div>

                      {/* Creation Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          建立時間
                        </label>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <CalendarIcon className="w-4 h-4" />
                          <span>{formatDateTimeWithSeconds(issue.createdAt)}</span>
                        </div>
                      </div>

                      {/* Due Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          到期時間
                        </label>
                        <DatePicker
                          value={issue.dueDate ? new Date(issue.dueDate) : null}
                          onChange={(date) => handleFieldSave('dueDate', date?.toISOString() || null)}
                          placeholder="設定到期時間"
                          className="w-full"
                        />
                      </div>

                      {/* Labels */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          標籤
                        </label>
                        <LabelPicker
                          value={issue.labels || []}
                          onChange={handleLabelsUpdate}
                          projectId={issue.projectId}
                          disabled={!canEditIssue}
                          placeholder="選擇標籤..."
                        />
                      </div>

                      {/* Custom Fields - Mobile */}
                      {projectCustomFields.length > 0 && (
                        <>
                          {projectCustomFields
                            .sort((a, b) => a.order - b.order)
                            .map((projectField) => {
                              const field = projectField.field
                              const currentValue = getCustomFieldValue(field.id)
                              const isEditing = editingField === `customField-${field.id}`

                              return (
                                <div key={field.id}>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {field.name}
                                    {projectField.required && (
                                      <span className="text-red-500 ml-1">*</span>
                                    )}
                                    <span className="text-xs text-gray-500 ml-2">
                                      ({getFieldTypeLabel(field.type)})
                                    </span>
                                  </label>

                                  {isEditing ? (
                                    <div>
                                      {field.type === 'TEXT' && (
                                        <input
                                          type="text"
                                          value={editingValue}
                                          onChange={(e) => {
                                            setEditingValue(e.target.value)
                                          }}
                                          onBlur={async (e) => {
                                            const value = e.target.value || null
                                            await handleCustomFieldUpdate(field.id, value)
                                            setEditingField(null)
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              const value = (e.target as HTMLInputElement).value || null
                                              handleCustomFieldUpdate(field.id, value)
                                              setEditingField(null)
                                            } else if (e.key === 'Escape') {
                                              setEditingField(null)
                                            }
                                          }}
                                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          autoFocus
                                          placeholder={`輸入${field.name}`}
                                        />
                                      )}

                                      {field.type === 'NUMBER' && (
                                        <input
                                          type="number"
                                          value={String(currentValue || '')}
                                          onChange={(e) => {
                                            const value = e.target.value ? Number(e.target.value) : null
                                            handleCustomFieldUpdate(field.id, value)
                                            setEditingField(null)
                                          }}
                                          onBlur={() => setEditingField(null)}
                                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          autoFocus
                                          placeholder={`輸入${field.name}`}
                                        />
                                      )}

                                      {field.type === 'DATE' && (
                                        <input
                                          type="date"
                                          value={currentValue ? String(currentValue).split('T')[0] : ''}
                                          onChange={(e) => {
                                            const value = e.target.value || null
                                            handleCustomFieldUpdate(field.id, value)
                                            setEditingField(null)
                                          }}
                                          onBlur={() => setEditingField(null)}
                                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          autoFocus
                                        />
                                      )}

                                      {field.type === 'SELECT' && field.options && (
                                        <select
                                          value={String(currentValue || '')}
                                          onChange={(e) => {
                                            const value = e.target.value || null
                                            handleCustomFieldUpdate(field.id, value)
                                            setEditingField(null)
                                          }}
                                          onBlur={() => setEditingField(null)}
                                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          autoFocus
                                        >
                                          <option value="">{`選擇${field.name}`}</option>
                                          {field.options.map((option) => (
                                            <option key={option} value={option}>
                                              {option}
                                            </option>
                                          ))}
                                        </select>
                                      )}
                                    </div>
                                  ) : (
                                    <div
                                      className="cursor-pointer hover:bg-gray-50 px-2 py-2 rounded transition-colors min-h-[40px] flex items-center"
                                      onClick={() => {
                                        if (canEditIssue) {
                                          setEditingField(`customField-${field.id}`)
                                          setEditingValue(String(currentValue || ''))
                                        }
                                      }}
                                      title={canEditIssue ? `點擊編輯${field.name}` : "無編輯權限"}
                                    >
                                      {currentValue ? (
                                        <span className="text-sm text-gray-700">
                                          {field.type === 'DATE' && currentValue
                                            ? new Date(String(currentValue)).toLocaleDateString('zh-TW')
                                            : String(currentValue)}
                                        </span>
                                      ) : (
                                        <span className="text-sm text-gray-500 italic">
                                          {canEditIssue ? `點擊設定${field.name}` : '未設定'}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                        </>
                      )}

                      {/* Last Updated */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          更新時間
                        </label>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <ClockIcon className="w-4 h-4" />
                          <span>{formatDateTimeWithSeconds(issue.updatedAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Comments Section - 直接在描述下方 */}
                <div>
                  {user && (
                    <CommentSection
                      issueId={issue.id}
                      comments={comments[issue.id] || []}
                      currentUser={user}
                      onAddComment={handleAddComment}
                      onUpdateComment={handleUpdateComment}
                      onDeleteComment={handleDeleteComment}
                      canCreateComment={canCreateComment}
                      canEditComment={canEditComment}
                      canDeleteComment={canDeleteComment}
                    />
                  )}
                </div>
              </div>

              {/* 右側欄位資訊 - 只在桌面版顯示 */}
              <div className="space-y-4 hidden lg:block">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-900 mb-4">議題詳情</h3>
                  
                  <div className="grid grid-cols-2 lg:grid-cols-1 gap-3 lg:gap-4 lg:space-y-0">
                    {/* Priority */}
                    <div>
                      <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">
                        優先級
                      </label>
                      {editingField === 'priority' ? (
                        <select
                          value={editData.priority}
                          onChange={(e) => {
                            const newPriority = e.target.value as IssuePriority
                            setEditData({ ...editData, priority: newPriority })
                            handleFieldSave('priority', newPriority)
                          }}
                          onBlur={() => setEditingField(null)}
                          className="w-full px-2 lg:px-3 py-1 lg:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          autoFocus
                        >
                          <option value={IssuePriority.HIGHEST}>最高</option>
                          <option value={IssuePriority.HIGH}>高</option>
                          <option value={IssuePriority.MEDIUM}>中</option>
                          <option value={IssuePriority.LOW}>低</option>
                          <option value={IssuePriority.LOWEST}>最低</option>
                        </select>
                      ) : (
                        <div 
                          className="flex items-center space-x-1 lg:space-x-2 cursor-pointer hover:bg-gray-50 px-1 lg:px-2 py-1 lg:py-2 rounded transition-colors"
                          onClick={() => handleFieldClick('priority')}
                          title="點擊修改優先級"
                        >
                          <div className={`p-1 rounded ${priority.bg}`}>
                            <PriorityIcon className={`h-3 lg:h-4 w-3 lg:w-4 ${priority.color}`} />
                          </div>
                          <span className="text-xs lg:text-sm text-gray-700">{priority.label}</span>
                        </div>
                      )}
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">
                        狀態
                      </label>
                      {editingField === 'status' ? (
                        <select
                          value={editData.status}
                          onChange={(e) => {
                            const newStatus = e.target.value as IssueStatus
                            setEditData({ ...editData, status: newStatus })
                            handleFieldSave('status', newStatus)
                          }}
                          onBlur={() => setEditingField(null)}
                          className="w-full px-2 lg:px-3 py-1 lg:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          autoFocus
                        >
                          <option value={IssueStatus.TODO}>待辦</option>
                          <option value={IssueStatus.IN_PROGRESS}>進行中</option>
                          <option value={IssueStatus.IN_REVIEW}>待審核</option>
                          <option value={IssueStatus.DONE}>完成</option>
                        </select>
                      ) : (
                        <span 
                          className={`px-2 py-1 text-xs font-medium rounded-full cursor-pointer hover:bg-opacity-75 transition-colors block text-center lg:inline ${status.color}`}
                          onClick={() => handleFieldClick('status')}
                          title="點擊修改狀態"
                        >
                          {status.label}
                        </span>
                      )}
                    </div>

                    {/* Assignee */}
                    <div className="col-span-2 lg:col-span-1">
                      <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">
                        負責人
                      </label>
                      {editingField === 'assignee' ? (
                        <select
                          value={editData.assigneeId || ''}
                          onChange={(e) => {
                            const assigneeId = e.target.value || null
                            setEditData({ ...editData, assigneeId })
                            handleFieldSave('assigneeId', assigneeId)
                          }}
                          onBlur={() => setEditingField(null)}
                          className="w-full px-2 lg:px-3 py-1 lg:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          autoFocus
                        >
                          <option value="">未指派</option>
                          {currentProject?.members?.map((member) => (
                            <option key={member.user.id} value={member.user.id}>
                              {member.user.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div 
                          className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 px-1 lg:px-2 py-1 lg:py-2 rounded transition-colors"
                          onClick={() => handleFieldClick('assignee')}
                          title="點擊指派負責人"
                        >
                          {issue.assignee ? (
                            <>
                              {issue.assignee.avatar ? (
                                <img
                                  src={issue.assignee.avatar}
                                  alt={issue.assignee.name}
                                  className="w-5 lg:w-6 h-5 lg:h-6 rounded-full"
                                />
                              ) : (
                                <div className="w-5 lg:w-6 h-5 lg:h-6 rounded-full bg-gray-300 flex items-center justify-center">
                                  <span className="text-xs font-medium text-gray-600">
                                    {issue.assignee.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <span className="text-xs lg:text-sm text-gray-700">{issue.assignee.name}</span>
                            </>
                          ) : (
                            <>
                              <UserCircleIcon className="w-5 lg:w-6 h-5 lg:h-6 text-gray-400" />
                              <span className="text-xs lg:text-sm text-gray-500">未指派</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Reporter */}
                    <div className="col-span-2 lg:col-span-1">
                      <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">
                        報告人
                      </label>
                      <div className="flex items-center space-x-2">
                        {issue.reporter.avatar ? (
                          <img
                            src={issue.reporter.avatar}
                            alt={issue.reporter.name}
                            className="w-5 lg:w-6 h-5 lg:h-6 rounded-full"
                          />
                        ) : (
                          <div className="w-5 lg:w-6 h-5 lg:h-6 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-xs font-medium text-gray-600">
                              {issue.reporter.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <span className="text-xs lg:text-sm text-gray-700">{issue.reporter.name}</span>
                      </div>
                    </div>

                    {/* Creation Date */}
                    <div>
                      <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">
                        建立時間
                      </label>
                      <div className="flex items-center space-x-1 lg:space-x-2 text-xs lg:text-sm text-gray-600">
                        <CalendarIcon className="w-3 lg:w-4 h-3 lg:h-4" />
                        <span>{formatDateTimeWithSeconds(issue.createdAt)}</span>
                      </div>
                    </div>

                    {/* Due Date */}
                    <div>
                      <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">
                        到期時間
                      </label>
                      <DatePicker
                        value={issue.dueDate ? new Date(issue.dueDate) : null}
                        onChange={(date) => handleFieldSave('dueDate', date?.toISOString() || null)}
                        placeholder="設定到期時間"
                        className="w-full text-xs lg:text-sm"
                      />
                    </div>

                    {/* Labels */}
                    <div className="col-span-2 lg:col-span-1">
                      <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">
                        標籤
                      </label>
                      <LabelPicker
                        value={issue.labels || []}
                        onChange={handleLabelsUpdate}
                        projectId={issue.projectId}
                        disabled={!canEditIssue}
                        placeholder="選擇標籤..."
                      />
                    </div>

                    {/* Custom Fields */}
                    {projectCustomFields.length > 0 && (
                      <>
                        {projectCustomFields
                          .sort((a, b) => a.order - b.order)
                          .map((projectField) => {
                            const field = projectField.field
                            const currentValue = getCustomFieldValue(field.id)
                            const isEditing = editingField === `customField-${field.id}`

                            return (
                              <div key={field.id} className="col-span-2 lg:col-span-1">
                                <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">
                                  {field.name}
                                  {projectField.required && (
                                    <span className="text-red-500 ml-1">*</span>
                                  )}
                                  <span className="text-xs text-gray-500 ml-2">
                                    ({getFieldTypeLabel(field.type)})
                                  </span>
                                </label>

                                {isEditing ? (
                                  <div>
                                    {field.type === 'TEXT' && (
                                      <input
                                        type="text"
                                        value={editingValue}
                                        onChange={(e) => {
                                          setEditingValue(e.target.value)
                                        }}
                                        onBlur={async (e) => {
                                          const value = e.target.value || null
                                          await handleCustomFieldUpdate(field.id, value)
                                          setEditingField(null)
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            const value = (e.target as HTMLInputElement).value || null
                                            handleCustomFieldUpdate(field.id, value)
                                            setEditingField(null)
                                          } else if (e.key === 'Escape') {
                                            setEditingField(null)
                                          }
                                        }}
                                        className="w-full px-2 lg:px-3 py-1 lg:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        autoFocus
                                        placeholder={`輸入${field.name}`}
                                      />
                                    )}

                                    {field.type === 'NUMBER' && (
                                      <input
                                        type="number"
                                        value={String(currentValue || '')}
                                        onChange={(e) => {
                                          const value = e.target.value ? Number(e.target.value) : null
                                          handleCustomFieldUpdate(field.id, value)
                                          setEditingField(null)
                                        }}
                                        onBlur={() => setEditingField(null)}
                                        className="w-full px-2 lg:px-3 py-1 lg:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        autoFocus
                                        placeholder={`輸入${field.name}`}
                                      />
                                    )}

                                    {field.type === 'DATE' && (
                                      <input
                                        type="date"
                                        value={currentValue ? String(currentValue).split('T')[0] : ''}
                                        onChange={(e) => {
                                          const value = e.target.value || null
                                          handleCustomFieldUpdate(field.id, value)
                                          setEditingField(null)
                                        }}
                                        onBlur={() => setEditingField(null)}
                                        className="w-full px-2 lg:px-3 py-1 lg:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        autoFocus
                                      />
                                    )}

                                    {field.type === 'SELECT' && field.options && (
                                      <select
                                        value={String(currentValue || '')}
                                        onChange={(e) => {
                                          const value = e.target.value || null
                                          handleCustomFieldUpdate(field.id, value)
                                          setEditingField(null)
                                        }}
                                        onBlur={() => setEditingField(null)}
                                        className="w-full px-2 lg:px-3 py-1 lg:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        autoFocus
                                      >
                                        <option value="">{`選擇${field.name}`}</option>
                                        {field.options.map((option) => (
                                          <option key={option} value={option}>
                                            {option}
                                          </option>
                                        ))}
                                      </select>
                                    )}
                                  </div>
                                ) : (
                                  <div
                                    className="cursor-pointer hover:bg-gray-50 px-1 lg:px-2 py-1 lg:py-2 rounded transition-colors min-h-[32px] flex items-center"
                                    onClick={() => {
                                      if (canEditIssue) {
                                        setEditingField(`customField-${field.id}`)
                                        setEditingValue(String(currentValue || ''))
                                      }
                                    }}
                                    title={canEditIssue ? `點擊編輯${field.name}` : "無編輯權限"}
                                  >
                                    {currentValue ? (
                                      <span className="text-xs lg:text-sm text-gray-700">
                                        {field.type === 'DATE' && currentValue
                                          ? new Date(String(currentValue)).toLocaleDateString('zh-TW')
                                          : String(currentValue)}
                                      </span>
                                    ) : (
                                      <span className="text-xs lg:text-sm text-gray-500 italic">
                                        {canEditIssue ? `點擊設定${field.name}` : '未設定'}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                      </>
                    )}

                    {/* Last Updated */}
                    <div>
                      <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">
                        更新時間
                      </label>
                      <div className="flex items-center space-x-1 lg:space-x-2 text-xs lg:text-sm text-gray-600">
                        <ClockIcon className="w-3 lg:w-4 h-3 lg:h-4" />
                        <span>{formatDateTimeWithSeconds(issue.updatedAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : activeTab === 'comments' ? (
            /* Comments Tab */
            <div className="space-y-4 md:space-y-6">
              {user && (
                <CommentSection
                  comments={comments[issue.id] || []}
                  issueId={issue.id}
                  currentUser={user}
                  onAddComment={handleAddComment}
                  onUpdateComment={handleUpdateComment}
                  onDeleteComment={handleDeleteComment}
                  canCreateComment={canCreateComment}
                  canEditComment={canEditComment}
                  canDeleteComment={canDeleteComment}
                />
              )}
            </div>
          ) : (
            /* Activity Tab */
            <div className="space-y-4 md:space-y-6">
              <ActivityLog
                activities={activities[issue.id] || []}
                loading={activityLoading}
              />
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

export default IssueDetailModal
